// Reactive "changes not yet saved on the server" count for the field UI.
//
// WHY (the capture-data-loss incident, C4 / #696). Counts are written
// local-first to PowerSync SQLite and uploaded asynchronously; if the upload
// jams or the count can never sync it is held on-device (the ps_crud queue or
// the `orphaned_capture_items` quarantine) while the app historically showed a
// success state. This hook surfaces that held work so the surveyor sees an
// honest "N changes not yet saved" indicator instead of false success.
//
// It REUSES `getHeldCaptureLoad()` (logout-cleanup.ts) — the same race-safe,
// fail-closed probe the C4 logout guard uses (#696) — and derives the displayed
// total from the SHARED `heldCaptureTotal`, so the indicator and the logout
// warning can never disagree about the COUNT or its WORDING. They intentionally
// differ on ONE thing — the fail-direction for an indeterminate read: the
// destructive logout dialog warns (fails closed), this ambient banner hides
// (never a false zero). count = quarantineCount + uploadQueueCount; `null` means
// the device could not be read this cycle (we show nothing rather than a false
// zero). The shared SUM/NOUN are identical; only the null handling diverges.
//
// Reactivity: a count drops shortly after an upload lands and rises shortly
// after one is quarantined/dropped — "shortly", not "instantly": refreshes are
// trailing-debounced (below), and the displayed value tracks what the probe
// READS, which for an upload event can be stale-HIGH for one window because the
// connector emits before `batch.complete()` drains ps_crud (the deferred #2
// emit-ordering; the poll/next event corrects it — always the safe over-report
// direction, never a false "all saved"). The three connector channels are
// unified through `subscribeToHeldChange` (upload-events.ts) and
// TRAILING-DEBOUNCED: a real queue drain emits once per uploaded entry,
// network-spaced, so each emit would
// otherwise start a fresh full probe (~2N SQLite COUNTs for an N-entry drain);
// the debounce collapses a drain to one probe per settle window (#732 / M6).
// online / tab-focus wakes and the poll stay IMMEDIATE (already spaced; the
// surveyor waits on the foreground re-probe). The bounded short poll is armed
// only while work is pending, the device is unreadable, OR the device is OFFLINE
// — offline is exactly when a +/- tap enqueues to ps_crud and fires NO connector
// event (uploads happen online), so a synced-then-offline surveyor would
// otherwise see a stale "all saved" (#732 / C2). An ONLINE confirmed-0 device
// disarms the poll entirely (events + fast uploads cover it), so it stops waking
// the SQLite layer until the next event/visibility/online/offline re-arms it
// (#732 / #7). DI seam (`load`, `pollMs`, `eventDebounceMs`) keeps it
// unit-testable without the PowerSync singleton.

import { useEffect, useState } from 'react'

import {
  getHeldCaptureLoad,
  heldCaptureTotal,
  type HeldCaptureLoad,
} from './logout-cleanup'
import { subscribeToHeldChange } from './upload-events'

export interface UnsyncedCaptureCount {
  /** True once at least one probe has resolved (avoids a first-render flash). */
  readonly ready: boolean
  /**
   * Changes held on this device that the server has NOT confirmed: pending
   * upload-queue entries + quarantined (un-syncable) counts. 0 once everything
   * has synced; `null` when the device couldn't be read this cycle (shown as
   * nothing, never as a false zero).
   */
  readonly count: number | null
}

export type LoadHeldCaptures = () => Promise<HeldCaptureLoad>

const DEFAULT_POLL_MS = 5000
// Trailing-debounce window for connector-event refreshes (M6). Long enough to
// collapse a network-spaced upload drain into one probe, short enough that the
// count lands ~immediately after the queue settles.
const DEFAULT_EVENT_DEBOUNCE_MS = 300

export function useUnsyncedCaptureCount(
  options: {
    load?: LoadHeldCaptures
    pollMs?: number
    eventDebounceMs?: number
  } = {},
): UnsyncedCaptureCount {
  const {
    load = getHeldCaptureLoad,
    pollMs = DEFAULT_POLL_MS,
    eventDebounceMs = DEFAULT_EVENT_DEBOUNCE_MS,
  } = options
  const [state, setState] = useState<UnsyncedCaptureCount>({
    ready: false,
    count: null,
  })

  useEffect(() => {
    let active = true
    let inFlight = false
    // A trigger (event / poll / wake) that arrived while a probe was in flight.
    // Collapses a mid-flight burst into exactly one trailing re-probe (below).
    let pending = false
    let timer: ReturnType<typeof setInterval> | null = null
    // Trailing-debounce timer for connector-event refreshes (M6). Distinct from
    // the poll `timer`: this collapses a network-spaced upload drain (one emit
    // per uploaded entry) into a single probe per settle window.
    let eventTimer: ReturnType<typeof setTimeout> | null = null
    // Latest known count, tracked outside React state so the poll-arming
    // decision (below) and the transient-error retention path don't depend on a
    // setState updater. `null` = unreadable / pre-first-resolve.
    let lastCount: number | null = null

    function disarmPoll(): void {
      if (timer !== null) {
        clearInterval(timer)
        timer = null
      }
    }

    // Publish `lastCount`, bailing when it is unchanged. Every probe (including
    // no-op poll/wake ticks on a held or offline device) would otherwise allocate
    // a fresh { ready, count } object → the provider's context value changes by
    // reference → the banner re-renders even when the number is identical.
    // Returning `prev` makes React skip that wasted render (audit-2 perf/fe).
    function publish(): void {
      setState((prev) =>
        prev.ready && prev.count === lastCount
          ? prev
          : { ready: true, count: lastCount },
      )
    }

    // Trailing-debounce a connector-event refresh. A real drain emits per
    // uploaded entry over the network; firing `refresh()` per emit means ~2N
    // serialized SQLite COUNTs for an N-entry drain (the in-flight `pending`
    // coalescer does NOT collapse them — a probe is two fast LOCAL reads that
    // resolve in the network gap between entries, so `inFlight` is already false
    // by the next emit). Debouncing collapses the whole drain to one probe once
    // the queue settles (#732 / M6). Poll/wake paths call `refresh()` directly,
    // un-debounced.
    function scheduleEventRefresh(): void {
      if (eventTimer !== null) clearTimeout(eventTimer)
      eventTimer = setTimeout(() => {
        eventTimer = null
        void refresh()
      }, eventDebounceMs)
    }

    // True when the device is offline. Offline is exactly when a +/- tap
    // enqueues to ps_crud and fires NO connector event (uploads — and their
    // `_emit*` — only happen online), so the only way to notice new offline
    // capture is to keep polling.
    function isOffline(): boolean {
      return typeof navigator !== 'undefined' && navigator.onLine === false
    }

    // Arm the short poll while the count could change WITHOUT a connector event:
    //   - work pending (`count > 0`) — a jammed/queued entry can drain,
    //   - device unreadable (`count === null`) — must be retried,
    //   - device OFFLINE — a fresh +/- tap enqueues to ps_crud and fires no
    //     event/online/visibility change, so an offline surveyor capturing after
    //     a synced checkpoint would otherwise see a stale "all saved" (#732 / C2,
    //     the re-review CRITICAL). The old unconditional poll caught this; the
    //     #7 quiesce dropped it — re-arming while offline restores it.
    // Only an ONLINE, confirmed-0 device disarms (events + fast uploads cover
    // it), so a synced+connected device still polls 0×/min.
    function syncPollArm(): void {
      if (lastCount === null || lastCount > 0 || isOffline()) {
        if (timer === null) {
          timer = setInterval(() => void refresh(), pollMs)
        }
      } else {
        disarmPoll()
      }
    }

    async function refresh(): Promise<void> {
      // A probe is two awaited SQLite reads; the poll tick and a burst of
      // connector events can fire together. Rather than stack concurrent reads
      // (or DROP a later trigger and read stale until the next poll), coalesce:
      // mark `pending` and let the in-flight probe run ONE more pass after it
      // resolves, so a mid-flight burst collapses to a single trailing re-probe
      // that reads the settled queue (#732 / #8).
      if (inFlight) {
        pending = true
        return
      }
      inFlight = true
      try {
        do {
          pending = false
          const held = await load()
          if (!active) return
          lastCount = heldCaptureTotal(held)
          publish()
        } while (pending && active)
      } catch {
        // Keep the last known count rather than flapping to null on a transient
        // read error; just mark ready so the UI isn't stuck pre-first-resolve.
        // `lastCount` is unchanged, so the poll stays armed (an error is an
        // unresolved/pending state, not a confirmed zero).
        if (active) publish()
      } finally {
        inFlight = false
        if (active) {
          // A trigger that arrived while a probe was THROWING never re-loops the
          // try's `do/while` (control jumped to `catch`), so it would be lost.
          // Honour it here: re-probe once so a connector event coincident with a
          // transient read failure is not dropped — even on an otherwise-synced
          // (lastCount === 0) device where `syncPollArm` would disarm (#732 / m6).
          if (pending) {
            pending = false
            queueMicrotask(() => void refresh())
          }
          syncPollArm()
        }
      }
    }

    void refresh()
    // One unified "held work may have changed" signal across the upload / drop /
    // quarantine channels — debounced (M6). Adding a fourth held-work channel
    // joins the signal in ONE place (subscribeToHeldChange) rather than needing a
    // remembered edit here (duplication / m2).
    const offHeldChange = subscribeToHeldChange(() => scheduleEventRefresh())
    // Reconnect always re-probes. visibilitychange re-probes only on the way
    // BACK to visible — a hidden/backgrounded tab is not being watched and any
    // change while hidden is caught by the visible-again fire (#732 / #10).
    // Firing on hide would also schedule a probe that could resolve against a
    // frozen tab. (Contrast use-auto-save.ts, which conversely *flushes* on the
    // way to hidden — save-before-freeze; this hook does the inverse edge.)
    // `offline` re-arms the poll so a fresh tap captured in a dead-zone after a
    // synced checkpoint is noticed even though it fires no connector event (C2).
    const onOnline = (): void => void refresh()
    const onOffline = (): void => syncPollArm()
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') void refresh()
    }
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      active = false
      disarmPoll()
      if (eventTimer !== null) clearTimeout(eventTimer)
      offHeldChange()
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [load, pollMs, eventDebounceMs])

  return state
}
